import { Fragment, useRef, useState } from 'react';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/core';
import { getListedEntryKey } from '../../../../utils/getListedEntryKey';
import { Color } from '../../../../styles/Color';
import { IMenuItemEntry } from '../../../../contentful/types/IMenuItem';
import SubNavigation from './SubNavigation';
import { RouterLink } from '../../link/RouterLink';
import { MainNavigationItemCommon } from '../Navigation.styles';
import { rotate } from '../../../../styles/animation/rotate';
import { getActivePageProps } from '../../../../navigation/getActivePageProps';
import { useCommonData } from '../../../../common-data/useCommonData';
import { getUrlForPageId } from '../../../../utils/getUrlForLink';
import {
	ChevronDownIcon,
	Css,
	ITheme,
	UnstyledLi,
	UnstyledUl,
} from '../../../../ui';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import MainNavigationUserSection from './MainNavigationUserSection';

interface IMainNavigationItemsProps {
	items: IMenuItemEntry[];
	siteSectionId?: string;
	variant?: 'gateway' | 'main';
}

export function MainNavigationItems({
	items,
	siteSectionId,
	variant = 'main',
}: IMainNavigationItemsProps) {
	const { pathToPageIdMap } = useCommonData();
	const [clickedItem, setClickedItem] = useState<IMenuItemEntry>();
	const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
	const subNavRef = useRef<HTMLUListElement>(null);

	const handleItemClick = (item: IMenuItemEntry) => {
		setClickedItem(item);
		setIsSubmenuOpen(!isSubmenuOpen);
	};

	useClickOutside(subNavRef, setIsSubmenuOpen, isSubmenuOpen);

	return (
		<StyledNav aria-label="main-navigation" variant={variant}>
			<StyledMainNavigationItemsList ref={subNavRef}>
				{items.map((item: IMenuItemEntry, index: number) => (
					<Fragment key={getListedEntryKey(item, index)}>
						<StyledMainNavigationListItem className="no-max-width">
							{!item.fields.subMenuItems?.length && item.fields.page ? (
								<StyledMainNavigationItemLink
									{...getActivePageProps(item, siteSectionId)}
									href={
										item.fields.page
											? getUrlForPageId(
													item.fields.page.fields.id,
													pathToPageIdMap,
											  )
											: ''
									}
									isActive={isSubmenuOpen && item === clickedItem}
									withThemeColors={variant === 'main'}
								>
									{item.fields.title}
								</StyledMainNavigationItemLink>
							) : (
								<StyledMainNavigationItem
									onClick={() => handleItemClick(item)}
									isActive={isSubmenuOpen && item === clickedItem}
									withThemeColors={variant === 'main'}
								>
									{item.fields.title} <ChevronDownIcon aria-label="expand" />
								</StyledMainNavigationItem>
							)}

							{item.fields?.subMenuItems?.length && (
								<SubNavigation
									subMenuItems={item.fields.subMenuItems}
									isOpen={isSubmenuOpen && item === clickedItem}
									withThemeColors={variant === 'main'}
								/>
							)}
						</StyledMainNavigationListItem>
					</Fragment>
				))}
			</StyledMainNavigationItemsList>
			<MainNavigationUserSection displaySearch={variant === 'main'} />
		</StyledNav>
	);
}

const StyledNav = styled.nav<{
	variant?: IMainNavigationItemsProps['variant'];
}>`
	display: flex;
	flex-direction: row;
	align-items: center;
	pointer-events: all;

	${({ variant }) => variant === 'main' && `width: 100%;`}

	${({ variant }) =>
		variant === 'gateway' &&
		css`
			.nav-user-section {
				margin-left: 50px;
			}
		`}
`;

const StyledMainNavigationItemsList = styled(UnstyledUl)`
	display: flex;
	flex-grow: 2;
	gap: ${Css.pxToRem(24)};
	align-items: center;
	pointer-events: all;
`;

export const StyledMainNavigationListItem = styled(UnstyledLi)`
	display: flex;
	align-items: center;
	position: relative;
	height: ${Css.pxToRem(40)};

	&:first-of-type {
		ul {
			border-top-left-radius: 0;
			border-top-right-radius: ${Css.pxToRem(16)};
			left: 0;
		}
	}

	&:last-child {
		ul {
			border-top-right-radius: 0;
			border-top-left-radius: ${Css.pxToRem(16)};
			right: 0;
		}
	}
`;

export const StyledMainNavigationItemLink = styled(RouterLink, {
	shouldForwardProp: (props) => isPropValid(props),
})<
	{
		isActive?: boolean;
		withThemeColors?: boolean;
	},
	ITheme
>`
	&:hover {
		color: ${Color.OnBackground};
	}

	text-decoration: none;
	${(props) =>
		MainNavigationItemCommon(
			props.theme,
			props.isActive,
			props.withThemeColors,
		)}
`;

const StyledMainNavigationItem = styled.button<
	{
		isActive: boolean;
		withThemeColors?: boolean;
	},
	ITheme
>`
	border: none;
	cursor: pointer;
	background: none;
	padding: 0;

	${(props) =>
		MainNavigationItemCommon(
			props.theme,
			props.isActive,
			props.withThemeColors,
		)}

	&:after {
		bottom: ${Css.pxToRem(6)};
	}

	svg {
		${({ isActive }) => rotate(isActive)}
	}
`;
